<!-- eslint-disable vue/component-name-in-template-casing -->
<template>
	<client-only>
		<div
			v-if="googleApi"
			class="relative h-150 w-auto md:sticky md:top-3xl"
			v-bind="$attrs"
		>
			<!-- Map placeholder shown until user requests the map -->
			<button
				v-if="!mapVisible"
				type="button"
				class="relative size-full overflow-hidden bg-gray-200"
				@click="mapVisible = true"
			>
				<nuxt-picture
					v-if="properties.placeholderImageSrc"
					:src="properties.placeholderImageSrc"
					:alt="t('blurred-map')"
					class="size-full object-cover blur-sm"
					loading="lazy"
					provider="storyblok"
					sizes="xs:100vw sm:640px md:768px lg:1024px xl:1280px 2xl:1536px"
					:img-attrs="{
						class: 'object-cover size-full blur-sm',
					}"
				/>

				<div v-else class="size-full bg-gray-200" />

				<div class="absolute inset-0 flex items-center justify-center">
					<p
						class="rounded bg-white/60 text-2xl font-bold shadow-[0_0_20px_15px_rgba(255,255,255,0.6)]"
					>
						{{ t("show-on-map") }}
					</p>
				</div>
			</button>

			<GoogleMap
				v-else
				ref="map"
				:api-key="googleApi"
				map-id="masku_map_id"
				style="width: 100%; height: 100%"
				:center="mapCenter"
				:zoom="zoom"
				@ready="mapReady = true"
				@map-tiles-loaded="mapReady = true"
			>
				<MarkerCluster v-if="(clusterMarkers ?? []).length > 1">
					<Marker
						v-for="markerLocation in clusterMarkers"
						:key="
							generateMarkerKey(markerLocation) + '-' + markerLocation.title
						"
						:options="{
							position: {
								lat: markerLocation.lat,
								lng: markerLocation.lng,
							},
						}"
						@click="handleMarkerClick(generateMarkerKey(markerLocation))"
					>
						<v-page-outlets-outlet-map-info
							:class="{
								hidden: activeInfoWindow !== generateMarkerKey(markerLocation),
								block: activeInfoWindow === generateMarkerKey(markerLocation),
							}"
							:outlet="markerLocation"
							:is-open="activeInfoWindow === generateMarkerKey(markerLocation)"
						/>
					</Marker>
				</MarkerCluster>

				<Marker
					v-else
					:options="{
						position: {
							lat: typeof selectedMarker === 'object' ? selectedMarker.lat : 0,
							lng: typeof selectedMarker === 'object' ? selectedMarker.lng : 0,
						},
					}"
				/>
			</GoogleMap>
		</div>
	</client-only>
</template>

<script setup lang="ts">
/** Developer notes
 * From this file you'll get following error randomly
 * "TypeError: can't access property "fromLatLngToDivPixel", e is undefined"
 * This is somehow related to Map Markers. I found issue on GitHub:
 * - https://github.com/inocan-group/vue3-google-map/issues/261#issuecomment-2182642866
 * BUT then this arises another issue regarding to AdvancedMarker slotted components. It seems to be broken
 * too in version 0.22:
 * https://github.com/inocan-group/vue3-google-map/issues/330
 * All we can do is to wait for the next version of the library to fix this.
 * We should remove the error logging from the sentry at this point and wait for the next version.
 */
import {GoogleMap, MarkerCluster, Marker} from "vue3-google-map";

import type {MapMarker} from "@pkg/shared/types/components/maps";

const properties = defineProps<{
	location: string;
	markers?: MapMarker[];
	filteredMarkers?: MapMarker[];
	placeholderImageSrc?: string;
}>();

const map = ref<InstanceType<typeof GoogleMap> | null>();
const mapReady = ref(false);
// useState makes the value stick between client-side navigations
const mapVisible = useState("outlet-map-visible", () => false);

const {t} = useI18n();

const zoom = ref(5);

const {
	public: {googleApi},
} = useRuntimeConfig();

interface MarkerLocation {
	title?: string;
	lat: number;
	lng: number;
}
// Generate unique key for marker.
const generateMarkerKey = (markerLocation: MarkerLocation) =>
	`${markerLocation.lat}-${markerLocation.lng}`;

const activeInfoWindow = ref<string | null>(null);

const handleMarkerClick = (markerId: string) => {
	activeInfoWindow.value = markerId;
};

// Function to split marker string into latitude and longitude.
const splitMarkers = (marker: string) => {
	const [latitude, longitude] = marker.split(",", 2);
	const lat = Number(latitude);
	const lng = Number(longitude);

	return {
		lat,
		lng,
	};
};
// Compute the selected marker. This is a singular marker that is selected from the outlet list
const selectedMarker = computed(() => {
	if (properties.location !== ",") {
		zoom.value = 15;
		return splitMarkers(properties.location);
	}

	return {lat: 0, lng: 0};
});

// Compute the center of the map based on the selected marker or the markers provided.
const mapCenter = computed(() => {
	if (typeof selectedMarker === "object" && selectedMarker.value.lat !== 0) {
		return {
			lat: selectedMarker.value.lat,
			lng: selectedMarker.value.lng,
		};
	}

	if ((properties.markers ?? []).length > 1) {
		zoom.value = 5;
		return {
			lat: Number(t("map.lat")),
			lng: Number(t("map.lng")),
		};
	}

	const singleMarkerCoordinates = splitMarkers(
		(properties.markers ?? [])[0]?.coordinates ?? ",",
	);

	return {
		lat: singleMarkerCoordinates.lat,
		lng: singleMarkerCoordinates.lng,
	};
});

// Ref for cluster markers. These markers will change based on the filteredMarkers prop.
// The prop will change based on the selected outlet in the filter (providence tabs).
const clusterMarkers = ref<MarkerLocation[]>([]);

// Watch for changes in filteredMarkers and update marker clusters accordingly.
watch(
	() => properties.filteredMarkers,
	(newMarkersData) => {
		zoom.value = 7; // Reset zoom when markers change
		clusterMarkers.value = (newMarkersData ?? []).map((marker) => ({
			...marker,
			...splitMarkers(marker.coordinates),
		}));
	},
	{immediate: true},
);

// Watch for changes in filteredMarkers to adjust zoom and fit bounds.
// This will ensure that the map adjusts its view based on the number of markers and zoom closer to the markers.
watch(
	() => properties.filteredMarkers,
	(newMarkersData) => {
		zoom.value = (newMarkersData ?? []).length > 1 ? 5 : 15; // Adjust zoom based on marker count.
		clusterMarkers.value = (newMarkersData ?? []).map((marker) => ({
			...marker,
			...splitMarkers(marker.coordinates),
		}));

		if (
			typeof google === "undefined" ||
			!google.maps ||
			!map.value ||
			mapCenter.value === null
		) {
			return;
		}

		// Listen to the cluster markers and set bound to the map based on the markers.
		const coords = clusterMarkers.value;
		if (mapReady && coords.length > 0) {
			map.value?.map?.fitBounds({
				north: Math.max(...coords.map((coord) => coord.lat)),
				south: Math.min(...coords.map((coord) => coord.lat)),
				west: Math.min(...coords.map((coord) => coord.lng)),
				east: Math.max(...coords.map((coord) => coord.lng)),
			});
		}
	},
	{immediate: true},
);
</script>

<i18n lang="json">
{
	"fi": {
		"show-on-map": "Näytä kartalla",
		"blurred-map": "Sumennettu kartta"
	},
	"et": {
		"show-on-map": "Näita kaardil",
		"blurred-map": "Hägustatud kaart"
	}
}
</i18n>
